import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { Grid, IconButton, Switch } from '@mui/material';
import { Percent } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faDollarSign } from '@fortawesome/free-solid-svg-icons';
import { FormatTypes, selecionarBenchmarks, useIsMobile } from '@monorepo-web/utils';
import {
  useFetchAPIs,
  useAppDispatch,
  useAppSelector,
  MoneyPrefix,
  useCustomItemOptions,
  sendAnalyticsData,
  fetchResumoCartPerf,
  setIsPercentToggle,
  setSelectedBenchmarks,
  formatCardsData,
  formatRowsRetorno,
  formatRowsClasse,
  formatRowsMovim,
  formatRowsLiq,
  formatRowsTabelaPorClasse,
  formatRowsTabelaPorCustodiante,
  formatPieChartClasse,
  formatPieChartCustodiante,
  getColumnsLiq,
  getColumnsRetorno,
  getColumnsClasse,
  getColumnsMovim,
  getColumnsModal,
} from '@monorepo-web/logic-extrato';
import {
  Cards,
  CustomModal,
  EnhancedTable,
  IData,
  ITableColumns,
  Line,
  GraficoBarra,
} from '@monorepo-web/components-library';

import {
  CardsResumoCarteira,
  GraficoAlocacao,
  TabelaFluxoResgate,
  TabelaPosicaoRendimento,
  TabelaResumoMovimentacoes,
  TabelaRetorno
} from '@monorepo-web/templates';

import HeaderWrapper from '../../MainLayout/HeaderWrapper';

interface Props {
  showCards?: boolean;
  showPieChart?: boolean;
  dispatchPDF?: boolean;
  sendData?: any;
  moneyPrefixParam?: string;
}

export default function ResumoCarteira({
  showCards,
  showPieChart,
  dispatchPDF,
  sendData,
  moneyPrefixParam,
}: Props) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const uselocation = useLocation();

  const [
    obj_datas,
    retorno,
    rendPorClasse,
    resumo,
    movimentacoes,
    alocClasse,
    alocCustodiante,
    loading,
    isLoaded,
    selectedBenchmarks,
    tabela_liquidez,
    dadosPercentResumo,
    loadingPercent,
    isLoadedPercent,
    isPercentToggle,
    carteiraList,
    loadedCarteiraList,
    carteira,
    isLoadingMinimized,
    params,
    preferenceCustomizacao,
  ] = useAppSelector((state) => [
    state.resumoCart.obj_datas,
    state.resumoCart.retorno,
    state.resumoCart.rendPorClasse,
    state.resumoCart.resumo,
    state.resumoCart.movimentacoes,
    state.resumoCart.alocClasse,
    state.resumoCart.alocCustodiante,
    state.resumoCart.loading,
    state.resumoCart.isLoaded,
    state.resumoCart.selectedBenchmarks,
    state.resumoCart.tabela_liquidez,
    state.resumoCart.dadosPercentResumo,
    state.resumoCart.loadingPercent,
    state.resumoCart.isLoadedPercent,
    state.resumoCart.isPercentToggle,
    state.preference.carteiraList,
    state.preference.loadedCarteiraList,
    state.preference.params.carteira,
    state.loadMinimized.isLoadingMinimized,
    state.preference.params,
    state.preference.preferenceStyle.customizacao,
  ]);

  const customItemOptions = useCustomItemOptions(loadedCarteiraList, carteiraList, carteira);
  const theme = useTheme();
  const toggleColorAbsolute = isPercentToggle ? 'gray' : theme.toggleSwitchColor;
  const toggleColorPercent = isPercentToggle ? theme.toggleSwitchColor : 'gray';

  const StyledSwitch = styled(Switch)(() => ({
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: theme.toggleSwitchColor,
    },
  }));

  const handleToggleResumo = async () => {
    dispatch(setIsPercentToggle(!isPercentToggle));
    if (!isLoadedPercent) {
      // evita chamar a API se já estiver carregado
      await dispatch(fetchResumoCartPerf());
    }
  };

  /**
   * Selecionar benchmarks
   * @param newBenchmarks Benchmarks selecionados
   * @param historyReplace Indica se a nova navegação no state do History vai adicionar uma nova entrada ou substituir a atual no history Stack
   * No caso do benchmark inicial(preferencia do usuario), a entrada vai substituir o state atual, adicionando os benchmarks no state
   * No caso dos benchmarks selecionados, será adicionado uma nova entrada na History, para guardar a navegação desses benchmarks no history Stack
   */
  const dispatchSelectedBenchmarks = (newBenchmarks: string[], historyReplace = false) => {
    const url = `?carteira=${carteira}&periodo=${params.periodo}`;
    // o parâmetro replace permite alterar o state sem adicionar uma nova entrada na history stack
    navigate(url, {
      replace: historyReplace,
      state: { ...uselocation.state, bench_resumo_cart: newBenchmarks },
    }); // adiciona os benchmarks selecionados no state do History router
    dispatch(setSelectedBenchmarks(newBenchmarks));
  };

  const moneyPrefix = moneyPrefixParam ?? MoneyPrefix(carteiraList, carteira);

  useEffect(() => {
    if (!dispatchPDF) document.title = `${(window as any).env.REACT_APP_TABNAME} - Resumo da Carteira`;
  }, []);

  useEffect(() => {
    if (isLoaded && !dispatchPDF) dispatch(sendAnalyticsData(location.href));
  }, [params, isLoaded]);

  useEffect(() => {
    selecionarBenchmarks(
      loadedCarteiraList,
      carteiraList,
      carteira,
      dispatchPDF,
      selectedBenchmarks,
      'resumoCart',
      dispatchSelectedBenchmarks,
    );
  }, [loadedCarteiraList]);

  const [checkLoaded, setCheckLoaded] = useState(isLoaded);
  useEffect(() => {
    if (isPercentToggle || (preferenceCustomizacao?.ResCart_percent ?? false)) {
      setCheckLoaded(isLoaded && isLoadedPercent);
    } else {
      setCheckLoaded(isLoaded);
    }
  }, [isLoaded, isLoadedPercent, isPercentToggle]);

  useFetchAPIs(isLoaded, loading, dispatchPDF);

  /**
   * Carregar os benchmarks quando a página for recarregada
   */
  useEffect(() => {
    const bench_history = uselocation?.state?.bench_resumo_cart ?? null;
    if (bench_history) {
      dispatch(setSelectedBenchmarks(bench_history));
    }
  }, []);

  const [openModal, setOpenModal] = useState(false);
  const [ativoModal, setAtivoModal] = useState('');
  const [modalType, setModalType] = useState('');

  const onClasseHandle = (value: any) => {
    setOpenModal(!openModal);
    setModalType('classe');
    if (value) setAtivoModal(value);
  };

  const onCustodianteHandle = (value: any) => {
    setOpenModal(!openModal);
    setModalType('custodiante');
    if (value) setAtivoModal(value);
  };
  const cardsData = isLoaded
    ? formatCardsData(resumo, preferenceCustomizacao, moneyPrefix, isMobile)
    : null;

  const rowsRetorno = isLoaded ? formatRowsRetorno(retorno, preferenceCustomizacao, moneyPrefix) : null;
  const columnsRetorno = getColumnsRetorno(params.periodo);

  const rowsClasse = isLoaded
    ? formatRowsClasse(
        rendPorClasse,
        dadosPercentResumo,
        isPercentToggle,
        preferenceCustomizacao,
        moneyPrefix,
      )
    : null;
  const columnsClasse = getColumnsClasse(preferenceCustomizacao, moneyPrefix);

  let rowsMovim: IData[] | null = [];
  let columnsMovim: ITableColumns[] = [];
  if (!(preferenceCustomizacao?.ResCart_removerTabMovim ?? false)) {
    rowsMovim = isLoaded ? formatRowsMovim(movimentacoes, moneyPrefix) : null;
    columnsMovim = getColumnsMovim();
  }

  const rowsLiq = isLoaded ? formatRowsLiq(tabela_liquidez) : null;
  const columnsLiq = getColumnsLiq();

  const rowsTabelaPorClasse = isLoaded
    ? formatRowsTabelaPorClasse(alocClasse, ativoModal, moneyPrefix, theme)
    : null;
  const rowsTabelaPorCustodiante = isLoaded
    ? formatRowsTabelaPorCustodiante(alocCustodiante, ativoModal, moneyPrefix, theme)
    : null;
  const colsModal = getColumnsModal();

  const pieChartClassData = isLoaded ? formatPieChartClasse(rendPorClasse, theme) : null;
  const pieChartCustodianteData = isLoaded
    ? formatPieChartCustodiante(alocCustodiante, preferenceCustomizacao)
    : null;

  const renderTogglePercent = (
    <Grid item style={{ marginLeft: 'auto' }}>
      <IconButton
        style={{ color: toggleColorAbsolute }}
        onClick={handleToggleResumo}
        disabled={isLoadingMinimized}>
        <FontAwesomeIcon
          icon={faDollarSign as IconProp}
          style={isLoadingMinimized ? { color: theme.disabledColor } : { color: toggleColorAbsolute }}
          size="xs"
        />
      </IconButton>
      <StyledSwitch
        checked={isPercentToggle}
        onChange={handleToggleResumo}
        style={isLoadingMinimized ? { color: theme.disabledColor } : { color: theme.toggleSwitchColor }}
        disabled={isLoadingMinimized}
      />
      <IconButton
        style={isLoadingMinimized ? { color: theme.disabledColor } : { color: toggleColorPercent }}
        onClick={handleToggleResumo}
        disabled={isLoadingMinimized}>
        <Percent />
      </IconButton>
    </Grid>
  );

  const renderTabRetorno = (
    <Grid
      item
      xs={12}
      md={
        preferenceCustomizacao?.ResCart_percent &&
        !(
          preferenceCustomizacao?.ResCart_removerTabMovim &&
          preferenceCustomizacao?.ResCart_removerTabFluxoResgate
        )
          ? 8
          : 12
      }>
      <TabelaRetorno
        rows={isLoaded ? rowsRetorno : null}
        periodo={params.periodo}
        datas={obj_datas}
      />
    </Grid>
  );

  const renderTabPosicao = (
    <TabelaPosicaoRendimento
      rows={isLoaded ? rowsClasse : null}
      titulo={`Posição e ${isPercentToggle ? 'Atribuição de Performance' : 'Rendimento'} por Classe`}
      renderTogglePercent={preferenceCustomizacao?.ResCart_percent ? null : renderTogglePercent}
      moneyPrefix={moneyPrefix}
      layout={preferenceCustomizacao?.ResCart_percent ? 'percent' : 'default'}
    />
  );

  const renderTabsFluxoEMov = (
    <Grid item xs={12} md={4}>
      {preferenceCustomizacao?.ResCart_removerTabFluxoResgate ?? false ? null : (
        <>
          <TabelaFluxoResgate rows={rowsLiq}/>
        </>
      )}
      {preferenceCustomizacao?.ResCart_removerTabMovim ?? false ? null : (
        <>
          <TabelaResumoMovimentacoes rows={rowsMovim} />
        </>
      )}
    </Grid>
  );

  const elementTabelas =
    preferenceCustomizacao?.ResCart_percent ?? false ? (
      <Grid container spacing={3} sx={{ marginTop: 3 }}>
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {renderTabRetorno}
            {renderTabsFluxoEMov}
          </Grid>
          {renderTabPosicao}
        </Grid>
      </Grid>
    ) : (
      <Grid container spacing={3} sx={{ marginTop: 3 }}>
        <Grid item xs={12} md={8}>
          {renderTabRetorno}
          {renderTabPosicao}
        </Grid>
        {renderTabsFluxoEMov}
      </Grid>
    );

  let barChartHorizontalHeight = 550;
  if (pieChartClassData && pieChartCustodianteData) {
    barChartHorizontalHeight =
      60 * Math.max(pieChartClassData.length, pieChartCustodianteData.length) + 50;
  }

  const elementClassCharts =
    preferenceCustomizacao?.ResCart_ClasseIsHorizontal ?? false ? (
      <GraficoAlocacao
        GraphComponent={GraficoBarra}
        data={pieChartClassData}
        height={barChartHorizontalHeight}
        onClickHandle={onClasseHandle}
        layout='barra'
        theme={theme}
      />
    ) : (
      <GraficoAlocacao
        data={pieChartClassData}
        onClickHandle={onClasseHandle}
      />
    );

  const elementCustodianteCharts =
    preferenceCustomizacao?.ResCart_CustodianteIsHorizontal ?? false ? (
      <GraficoAlocacao
        GraphComponent={GraficoBarra}
        data={pieChartCustodianteData}
        titulo='Alocação por Custodiante'
        height={barChartHorizontalHeight}
        onClickHandle={onCustodianteHandle}
        layout='barra'
        theme={theme}
      />
    ) : (
      <GraficoAlocacao
        data={pieChartCustodianteData}
        titulo='Alocação por Custodiante'
        onClickHandle={onCustodianteHandle}
        moneyPrefix={moneyPrefix}
        FormatTypeParam={
          preferenceCustomizacao?.ResCart_graficoAlocacaoVolume ?? false
            ? FormatTypes.monetary_no_space
            : FormatTypes.percentage
        }
      />
    );

  useEffect(() => {
    if (dispatchPDF) {
      const data = {
        resumo,
        periodo: params.periodo,
        rowsRetorno,
        columnsRetorno,
        rowsLiq,
        columnsLiq,
        rowsClasse,
        columnsClasse,
        rowsMovim,
        columnsMovim,
        pieChartClassData,
        pieChartCustodianteData,
        obj_datas,
        moneyPrefix,
      };
      sendData('ResCart', data);
    }
  }, []);

  if (dispatchPDF) {
    return <></>;
  }

  return (
    <>
      <Grid>
        <HeaderWrapper
          title="Resumo da Carteira"
          containerAux
          benchmarks={{ selectedBenchmarks, dispatchSelectedBenchmarks, customItemOptions }}
          periodo
          obj_datas={obj_datas}
        />
        <Line />
      </Grid>
      <Grid container columns={1} display={showCards ? 'grid' : 'none'}>
        <CardsResumoCarteira
          info={isLoaded ? resumo : null}
          moneyPrefix={moneyPrefix}
          layout={isMobile ? 'mobile':'default'}
          replaceLabelCards={{
            saldo1: preferenceCustomizacao?.ResCart_textoCard1,
            saldo2: preferenceCustomizacao?.ResCart_textoCard5,
          }}
        />
      </Grid>
      {elementTabelas}
      <Grid
        container
        spacing={2}
        justifyContent="center"
        alignItems="center"
        display={showPieChart ? 'flex' : 'none'}>
        {pieChartClassData && pieChartClassData.length > 0 && !isNaN(pieChartClassData[0].value) ? (
          <Grid item xs={12} lg={6} xl={6} marginBottom={{ xs: 15, lg: 0 }}>
            {elementClassCharts}
          </Grid>
        ) : null}
        {(preferenceCustomizacao?.ResCart_mostrarGraficoAlocacao ?? true) &&
        pieChartCustodianteData &&
        pieChartCustodianteData.length > 0 &&
        !isNaN(pieChartCustodianteData[0].value) ? (
          <Grid item xs={12} lg={6} xl={6}>
            {elementCustodianteCharts}
          </Grid>
        ) : null}
      </Grid>

      <CustomModal open={openModal} onCloseHandle={() => setOpenModal(!openModal)} width="fit-content">
        <EnhancedTable
          rows={modalType === 'classe' ? rowsTabelaPorClasse : rowsTabelaPorCustodiante}
          columns={colsModal}
          height={300}
        />
      </CustomModal>
    </>
  );
}
